// layout.card.v1
//
// A self-contained content block with elevated background + border. Used
// for KPIs, form groups, summary cards, etc. Smaller surface area than
// Section — no built-in title slot here on purpose; if you need a title,
// nest a Section inside.

import { type JSX, splitProps } from "solid-js"

export type CardElevation = "flat" | "raised"

export interface CardProps {
  elevation?: CardElevation
  interactive?: boolean
  children?: JSX.Element
  class?: string
  classList?: Record<string, boolean | undefined>
  onClick?: (event: MouseEvent) => void
  role?: JSX.HTMLAttributes<HTMLDivElement>["role"]
  tabIndex?: number
  ariaLabel?: string
}

const elevationClasses = {
  flat: "bg-background-base border border-border-weaker-base",
  raised: "bg-surface-raised-base border border-border-weak-base",
} as const satisfies Record<CardElevation, string>

export function Card(props: CardProps) {
  const [local, rest] = splitProps(props, [
    "elevation",
    "interactive",
    "children",
    "class",
    "classList",
    "onClick",
    "role",
    "tabIndex",
    "ariaLabel",
  ])

  return (
    <div
      data-component="layout.card"
      data-elevation={local.elevation ?? "flat"}
      data-interactive={local.interactive ? "true" : undefined}
      role={local.role}
      tabIndex={local.tabIndex}
      aria-label={local.ariaLabel}
      onClick={local.onClick}
      classList={{
        "rounded-xl p-4 min-w-0": true,
        [elevationClasses[local.elevation ?? "flat"]]: true,
        "transition-colors duration-150 cursor-pointer hover:border-border-weak-base hover:bg-surface-raised-base-hover":
          !!local.interactive,
        [local.class ?? ""]: !!local.class,
        ...(local.classList ?? {}),
      }}
      {...rest}
    >
      {local.children}
    </div>
  )
}
